'use client';

/**
 * THE ONE-TIME TIDY (the Stage algorithm, rule 21; open question 5,
 * answered with the proposal, 2026-09-29, lane D).
 *
 * WHY IT EXISTS. Until today every app a person opened stored a sheet named
 * after it, and closing the app left the sheet behind: the owner's Stage had
 * 26 screens, 11 of them empty and named after apps long gone (Verdict,
 * REDA, Writer 2, Writer 3, ...). Opening an app no longer stores a screen
 * (rule 1), so no new ones pile up, but the old ones stay, because they are
 * KEPT screens now (rule 11): an old sheet cannot tell a name the system gave
 * it from one a person typed, so nothing removes them on its own.
 *
 * WHAT IT DOES. Stage offers once, per workspace and per device: "11 screens
 * have nothing on them. Remove them." A press removes exactly the sheets
 * `emptyScreenSheets` lists (no window on them, a minimized one included, and
 * nothing a person made), and offers Undo, which puts every sheet back as it
 * was (`restoreItem`, same id and state). Dismissing it, or using it, ends
 * the offer on this device for good.
 *
 * NEVER AUTOMATIC: nothing here runs without the press. No effect, timer or
 * mount removes a sheet (pinned by tests/unit/workspaces/stage-tidy.test.tsx
 * and the source guard in stage-lane-d-guards.test.ts). The remembered answer
 * is a per-device convenience in `localStorage`, wrapped for storage that
 * throws; without it the offer simply shows again, still asking first.
 *
 * ONLY OLD SCREENS (fix round 2, 2026-09-30). The offer appeared the moment
 * a person made a new screen, offering to remove it: a brand-new screen is
 * empty too. It now lists only sheets the workspace already had when this
 * page loaded it (`tidyOfferSheets` over the store's `loadedItemIds`, a
 * snapshot, not the items' `createdAt`; the store says why), so a screen made
 * during this session, by anyone, is never offered.
 *
 * A DESKTOP ONLY (2026-10-03). The phone's Stage is a list of open apps and
 * draws no screens (stage-sheet.tsx), so an offer about empty screens had
 * nothing on the page to refer to there; its inline "sheet" variant went
 * with it. The sheets themselves are untouched and the desktop still offers.
 */

import { useEffect, useMemo, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import {
  useLoadedItemIds,
  useWorkspace,
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { tidyOfferSheets } from '@/lib/workspaces/stage-rules';
import type { WorkspaceItemDTO } from '@/lib/workspaces/types';

/** Per workspace, per device: the offer was used or dismissed. */
export function tidyOfferKey(workspaceId: string): string {
  return `ws-tidy-offer:${workspaceId}`;
}

function readAnswered(workspaceId: string): boolean {
  try {
    return window.localStorage.getItem(tidyOfferKey(workspaceId)) !== null;
  } catch {
    return false;
  }
}

function writeAnswered(workspaceId: string, answer: 'removed' | 'dismissed'): void {
  try {
    window.localStorage.setItem(tidyOfferKey(workspaceId), answer);
  } catch {
    /* storage blocked: the offer may come back next time, still asking first */
  }
}

export function StageTidyOffer() {
  const t = useT();
  const workspace = useWorkspace();
  const items = useWorkspaceItems();
  const loadedIds = useLoadedItemIds();
  const { removeItem, restoreItem } = useWorkspaceActions();
  // Null until storage is read after mount, so nothing flashes on the server
  // render or for a person who already answered.
  const [answered, setAnswered] = useState<boolean | null>(null);
  const [removed, setRemoved] = useState<WorkspaceItemDTO[] | null>(null);
  useEffect(() => {
    setAnswered(readAnswered(workspace.id));
  }, [workspace.id]);
  const empty = useMemo(() => tidyOfferSheets(items, loadedIds), [items, loadedIds]);

  const shell =
    'pointer-events-auto absolute left-1/2 top-3 z-30 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-border/60 bg-popover px-3 py-2 text-sm text-foreground shadow-lg';

  if (removed) {
    const count = removed.length;
    return (
      <div role="status" data-testid="stage-tidy-undo" className={shell}>
        <span className="min-w-0 flex-1 truncate">
          {count === 1
            ? t('stage.removedEmptyScreen')
            : t('stage.removedEmptyScreens', { count })}
        </span>
        <button
          type="button"
          data-testid="stage-tidy-undo-button"
          onClick={() => {
            for (const sheet of removed) restoreItem(sheet);
            setRemoved(null);
          }}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-xs font-medium transition-colors hover:bg-accent"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {t('stage.undo')}
        </button>
        <button
          type="button"
          aria-label={t('actions.dismiss')}
          onClick={() => setRemoved(null)}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  if (answered !== false || empty.length === 0) return null;
  const count = empty.length;

  return (
    <div role="region" aria-label={t('stage.emptyScreensRegion')} data-testid="stage-tidy-offer" className={shell}>
      <span className="min-w-0 flex-1 truncate">
        {count === 1 ? t('stage.emptyScreensOfferOne') : t('stage.emptyScreensOffer', { count })}
      </span>
      <button
        type="button"
        data-testid="stage-tidy-remove"
        onClick={() => {
          // Read the list again at the press: a peer may have put something
          // on one of them since the offer was drawn.
          const sheets = tidyOfferSheets(items, loadedIds);
          if (sheets.length === 0) return;
          for (const sheet of sheets) void removeItem(sheet.id);
          writeAnswered(workspace.id, 'removed');
          setAnswered(true);
          setRemoved(sheets);
        }}
        className="inline-flex shrink-0 items-center rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {count === 1 ? t('stage.removeEmptyScreen') : t('stage.removeEmptyScreens')}
      </button>
      <button
        type="button"
        aria-label={t('actions.dismiss')}
        data-testid="stage-tidy-dismiss"
        onClick={() => {
          writeAnswered(workspace.id, 'dismissed');
          setAnswered(true);
        }}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
